/**
 * The two callables the admin app invokes.
 *
 * `requestRetry` reopens a render that errored or went stale. It is the only path back for a
 * failed **global** pre-render: a global carries no `requestedBy`, so its record belongs to
 * nobody, and the callable widens ownership for exactly that case when the caller holds the
 * `admin` claim (`functions/src/retry.ts`). Without it a first failed pre-render would leave
 * a permanently dead content address.
 *
 * Deliberately not here: `deletePrimitive`. There is **no permanent delete of a global** in
 * this app (`admin/01`), and the callable is author-scoped anyway.
 */

import { REGION } from '@atlas-nook/brand';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import type { Functions } from 'firebase/functions';

import { adminEnv, EMULATOR } from '../env.js';
import { firebaseApp } from '../firebase.js';

let cached: Functions | null = null;

function functions(): Functions {
  if (cached === null) {
    cached = getFunctions(firebaseApp(), REGION);
    if (adminEnv().useEmulator) {
      connectFunctionsEmulator(cached, EMULATOR.functionsHost, EMULATOR.functionsPort);
    }
  }
  return cached;
}

/** `costsMoney` comes back from the server rather than being assumed here. */
export async function requestRetryCall(
  id: string,
): Promise<{ ok: boolean; costsMoney: boolean; refusal?: string }> {
  const call = httpsCallable<{ collection: string; id: string }, { ok: true; costsMoney: boolean }>(
    functions(),
    'requestRetry',
  );
  try {
    const result = await call({ collection: 'variantInstances', id });
    return result.data;
  } catch (error) {
    const failure = error as { details?: { code?: string }; message?: string };
    return {
      ok: false,
      costsMoney: false,
      refusal: failure.details?.code ?? failure.message ?? 'failed',
    };
  }
}

/** One of the three auth mails — the sign-in link, verify, reset (`general/08`). */
export interface AuthMailRequest {
  kind: 'signInLink' | 'verify' | 'reset';
  email: string;
  lang: 'en' | 'tr';
  app: 'studio' | 'admin';
}

/**
 * Ask the backend to send one of the three auth mails — the same callable the Studio uses,
 * with `app: 'admin'` so the link comes back to this app. Resolves for a sent mail, an unknown
 * address and a rate-limited one alike; throws only when the mail could not be sent.
 */
export async function requestAuthMailCall(request: AuthMailRequest): Promise<void> {
  const call = httpsCallable<AuthMailRequest, { ok: true }>(functions(), 'requestAuthMail');
  await call(request);
}
